import {Injectable} from "@angular/core";
import {ReportError} from "app/app.service";
import {ApiService} from "app/services/domain/api.service";
import * as Models from "app/services/proxy/model/models";

/**
 * Baseline configuration for AI unit economics.
 *
 * A baseline records what an AI workload replaced — manual duration, quality scores and
 * realized value per primary key — so a later job can attach actual cost to each entry.
 */
@Injectable()
export class AIUnitEconomicsService
{
    constructor(private readonly m_api: ApiService)
    {}

    /**
     * Parse an uploaded spreadsheet without saving it.
     *
     * Returns the entries it found plus row-level errors and warnings, so the caller can show
     * the user everything wrong with the file in one pass rather than one problem per upload.
     */
    @ReportError
    public async parseBaselineFile(request: Models.AIUnitEconomicsBaselineParseRequest): Promise<Models.AIUnitEconomicsBaselineParseResponse>
    {
        return await this.m_api.aiUnitEconomics.aIUnitEconomicsParseBaselineFile(request);
    }

    @ReportError
    public async createBaseline(request: Models.AIUnitEconomicsBaselineRequest): Promise<Models.AIUnitEconomicsBaselineResponse>
    {
        return await this.m_api.aiUnitEconomics.aIUnitEconomicsCreateBaseline(request);
    }

    /**
     * List baseline identifiers for a page. Pair with {@link batchBaselines} to hydrate them.
     */
    @ReportError
    public async listBaselines(request: Models.AIUnitEconomicsBaselineListRequest): Promise<Models.ListResponse>
    {
        return await this.m_api.aiUnitEconomics.aIUnitEconomicsListBaselines(request);
    }

    @ReportError
    public async batchBaselines(request: Models.AIUnitEconomicsBaselineBatchRequest): Promise<Models.AIUnitEconomicsBaselineBatchResponse>
    {
        return await this.m_api.aiUnitEconomics.aIUnitEconomicsBatchBaselines(request);
    }

    @ReportError
    public async getBaseline(baselineId: string): Promise<Models.AIUnitEconomicsBaselineResponse>
    {
        return await this.m_api.aiUnitEconomics.aIUnitEconomicsGetBaseline(baselineId);
    }

    @ReportError
    public async updateBaseline(baselineId: string,
                                request: Models.AIUnitEconomicsBaselineRequest): Promise<Models.AIUnitEconomicsBaselineResponse>
    {
        return await this.m_api.aiUnitEconomics.aIUnitEconomicsUpdateBaseline(baselineId, request);
    }

    @ReportError
    public async deleteBaseline(baselineId: string): Promise<void>
    {
        return await this.m_api.aiUnitEconomics.aIUnitEconomicsDeleteBaseline(baselineId);
    }

    /**
     * List consolidated cost bucket identifiers.
     *
     * Paged by the caller: KPIs are computed client-side over the whole slice, so the caller
     * pages until every id is fetched, then hydrates them through {@link batchCost}.
     */
    @ReportError
    public async listCost(request: Models.AIUnitEconomicsCostListRequest): Promise<Models.ListResponse>
    {
        return await this.m_api.aiUnitEconomics.aIUnitEconomicsListCost(request);
    }

    @ReportError
    public async batchCost(request: Models.BatchRequest): Promise<Models.AIUnitEconomicsCostBatchResponse>
    {
        return await this.m_api.aiUnitEconomics.aIUnitEconomicsBatchCost(request);
    }
}
